/**
 * DevCycle Feature Flag Configuration
 *
 * This module configures DevCycle for feature flag management and remote config.
 * DevCycle allows you to control feature rollouts, A/B testing, and configuration
 * without deploying new code.
 *
 * Setup Instructions:
 * 1. Go to DevCycle Dashboard (https://app.devcycle.com)
 * 2. Create a new project or use existing Influenster project
 * 3. Get the Client SDK Key from Settings → Environments → [Your Environment]
 * 4. Add VITE_DEVCYCLE_CLIENT_SDK_KEY to your environment variables
 *
 * Type Generation:
 * Run `npm run dvc:generate` to generate TypeScript types from DevCycle dashboard.
 * This requires DVC_CLIENT_ID and DVC_CLIENT_SECRET environment variables.
 *
 * Note: Use the CLIENT SDK key (not Server SDK key) for web applications
 */

import { isDev } from './env';

/**
 * Get DevCycle SDK key based on environment
 * Different environments should use different SDK keys for proper targeting
 */
export const getDevCycleConfig = () => {
  const sdkKey = import.meta.env.VITE_DEVCYCLE_CLIENT_SDK_KEY || '';

  if (!sdkKey && !isDev()) {
    console.warn('[DevCycle] SDK key not configured. Set VITE_DEVCYCLE_CLIENT_SDK_KEY environment variable.');
  }

  return {
    sdkKey,
    // DevCycle options
    options: {
      // Log level for debugging
      logLevel: isDev() ? ('debug' as const) : ('error' as const),
      // Disable automatic event logging in dev to reduce noise
      disableAutomaticEventLogging: isDev(),
      // Disable custom event logging in dev
      disableCustomEventLogging: isDev(),
    },
  };
};

export default {
  getDevCycleConfig,
};
